// src/components/notifications/NotificationItem.tsx
import { ActionIcon, Box, Group, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useNavigate } from "@/routing/accountRouting";
import type { AppNotification } from "@/api/notifications";

const TYPE_BORDER_COLOR: Record<string, string> = {
  success: "var(--mantine-color-green-6)",
  error: "var(--mantine-color-red-6)",
  warning: "var(--mantine-color-yellow-6)",
  info: "var(--mantine-color-blue-6)",
};

type Props = {
  notification: AppNotification;
  onMarkAsRead: (publicId: string) => void;
  onDismiss: (publicId: string) => void;
  onClosePanel: () => void;
};

// Defense-in-depth: backend validates action_url is a relative path
// (apps/backend/api/schemas/notification_schemas.py::_validate_action_url),
// but we double-check on the frontend to refuse navigation if a stale or
// malformed value ever slips through. Returns true only for same-origin
// relative paths starting with "/".
// Source: docs/security-reviews/2026-05-01-data-breach-review.md HIGH #4.
function isSafeInternalUrl(url: string): boolean {
  if (!url.startsWith("/")) return false;
  if (url.startsWith("//")) return false; // protocol-relative
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}

export default function NotificationItem({
  notification,
  onMarkAsRead,
  onDismiss,
  onClosePanel,
}: Props) {
  const navigate = useNavigate();
  const borderColor = TYPE_BORDER_COLOR[notification.type] || TYPE_BORDER_COLOR.info;
  const safeActionUrl =
    notification.action_url && isSafeInternalUrl(notification.action_url)
      ? notification.action_url
      : null;

  const handleClick = () => {
    if (!notification.is_read) {
      onMarkAsRead(notification.public_id);
    }
    if (safeActionUrl) {
      navigate(safeActionUrl);
      onClosePanel();
    } else if (notification.action_url) {
      // Backend should reject these, but log a breadcrumb if one ever arrives.
      // eslint-disable-next-line no-console
      console.warn(
        "Refusing to navigate to non-internal notification action_url:",
        notification.action_url
      );
    }
  };

  const timeAgo = formatDistanceToNow(parseISO(notification.created_at), {
    addSuffix: true,
  });

  return (
    <Box
      onClick={handleClick}
      style={{
        padding: "10px 12px",
        borderLeft: `3px solid ${borderColor}`,
        backgroundColor: notification.is_read
          ? undefined
          : "var(--mantine-color-blue-0)",
        cursor: safeActionUrl ? "pointer" : "default",
        borderBottom: "1px solid var(--mantine-color-gray-2)",
      }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} align="center">
            {!notification.is_read && (
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "var(--mantine-color-blue-6)",
                  flexShrink: 0,
                }}
              />
            )}
            <Text size="sm" fw={500} truncate>
              {notification.title}
            </Text>
          </Group>
          {notification.message && (
            <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
              {notification.message}
            </Text>
          )}
          <Text size="xs" c="dimmed" mt={4}>
            {timeAgo}
          </Text>
        </Box>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(notification.public_id);
          }}
          aria-label="Dismiss notification"
        >
          <IconX size={14} />
        </ActionIcon>
      </Group>
    </Box>
  );
}
